import dayjs from 'utils/extendedDayJS';
import { getConfig } from 'config/baseConfig';
import axios from 'config/axios';

export const CSV_OPTIONS = Object.freeze({
  GUESTS: 0,
  QUESTIONNAIRE: 1,
  FEEDBACK: 2,
  CONTENT_ENGAGEMENT: 3,
  CONTENT_FEEDBACK: 4,
  COMMUNITY_MEMBERS: 5,
  INSIGHTS: 6
});

export const CSV_QUEUE_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

/**
 * Parse blob response into JSON.
 */
const fileRead = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(JSON.parse(reader.result));
    };

    reader.onerror = reject;

    reader.readAsText(file);
  });
};

/**
 * Grab status of the csv rq job.
 * Used as long polling to backend request to grab status of CSV generation of the job.
 * @param {*} resource
 * @param {*} param1
 * @returns
 */
export const getCSVReportById = async (resource, { exportParams, csvReportId }) => {
  const today = dayjs().format('YYYY_MM_DD_HH_mm');
  let fileName;
  const apiURL = getConfig('API_URL');
  const downloadUrl = `${apiURL}/csv/${csvReportId}`;

  try {
    const response = await axios({ method: 'get', url: downloadUrl, responseType: 'blob' });

    switch (resource) {
      case CSV_OPTIONS.GUESTS:
        fileName = `${today}_event_${exportParams}_guests.csv`;
        break;
      case CSV_OPTIONS.FEEDBACK:
        fileName = `${today}_event_${exportParams}_feedback.csv`;
        break;
      case CSV_OPTIONS.QUESTIONNAIRE:
        fileName = `${today}_event_${exportParams}_questionnaire.csv`;
        break;
      case CSV_OPTIONS.CONTENT_ENGAGEMENT:
        fileName = `${today}_content_${exportParams}_engagement.csv`;
        break;
      case CSV_OPTIONS.CONTENT_FEEDBACK:
        fileName = `${today}_content_${exportParams}_feedback.csv`;
        break;
      case CSV_OPTIONS.COMMUNITY_MEMBERS:
        fileName = `${today}_community_${exportParams}_members.csv`;
        break;
      case CSV_OPTIONS.INSIGHTS: {
        const { userId, startDate, endDate } = exportParams;
        const start = dayjs(startDate).format('YYYY-MM-DD');
        const end = dayjs(endDate).format('YYYY-MM-DD');
        fileName = `insights_user_${userId}_${start}_${end}.csv`;
        break;
      }
      default:
        throw new Error(`Could not retrieve csv`);
    }

    const isDataCSV = response.headers['content-type'].includes('csv');

    // If response type is not csv, check for the response JSON object to see if 'status' is returned.
    if (!isDataCSV) {
      const JSONResponse = await fileRead(response.data);

      // If there is a 'status' property returned with 'pending' - return the proper value.
      if (JSONResponse?.status && JSONResponse.status === CSV_QUEUE_STATUS.PENDING) {
        return { status: CSV_QUEUE_STATUS.PENDING };
      }

      return { status: CSV_QUEUE_STATUS.FAILED, message: `Could not retrieve CSV for ${fileName}` };
    }

    const dataBlobs = response.data;

    if (dataBlobs) {
      const url = window.URL.createObjectURL(dataBlobs);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      return { status: CSV_QUEUE_STATUS.COMPLETED };
    }
  } catch (e) {
    // Handle failure.
    return { status: CSV_QUEUE_STATUS.FAILED, message: e.message };
  }

  return { status: CSV_QUEUE_STATUS.PENDING };
};

/**
 * Request generation of CSV file.
 * Note: For 'attendees', the generation job will be queued and we use `getCSVReportById` to retreive status of the job and the data.
 *
 * @param {*} resource
 * @param {*} param1
 * @returns
 */
export const generateCSV = async (resource, { exportParams, timezoneCode }) => {
  let downloadUrl;
  const apiURL = getConfig('API_URL');

  switch (resource) {
    case CSV_OPTIONS.GUESTS:
      downloadUrl = `${apiURL}/csv/guests/${exportParams}?timezone=${timezoneCode}`;
      break;
    case CSV_OPTIONS.FEEDBACK:
      downloadUrl = `${apiURL}/csv/event/feedback/${exportParams}?timezone=${timezoneCode}`;
      break;
    case CSV_OPTIONS.QUESTIONNAIRE:
      downloadUrl = `${apiURL}/csv/questionnaire/${exportParams}?timezone=${timezoneCode}`;
      break;
    case CSV_OPTIONS.CONTENT_ENGAGEMENT:
      downloadUrl = `${apiURL}/csv/content_engagement/${exportParams}?timezone=${timezoneCode}`;
      break;
    case CSV_OPTIONS.CONTENT_FEEDBACK:
      downloadUrl = `${apiURL}/csv/content/feedback/${exportParams}?timezone=${timezoneCode}`;
      break;
    case CSV_OPTIONS.COMMUNITY_MEMBERS:
      downloadUrl = `${apiURL}/csv/community/members/${exportParams}?timezone=${timezoneCode}`;
      break;
    case CSV_OPTIONS.INSIGHTS: {
      const { startDate, endDate } = exportParams;
      if (!startDate || !endDate || !dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
        throw new Error('A valid date range is required to export Insights data.');
      }
      const startISO = encodeURIComponent(dayjs(startDate).toISOString());
      const endISO = encodeURIComponent(dayjs(endDate).endOf('day').toISOString());
      const insightsResponse = await axios.get(
        `${apiURL}/csv/insights?start_date=${startISO}&end_date=${endISO}`
      );
      return insightsResponse.data;
    }
    default:
      throw new Error('No resource specified.');
  }

  const fetchResponse = await fetch(downloadUrl, {
    credentials: 'include'
  });

  if (!fetchResponse.ok) {
    throw new Error('Failed to fetch CSV data.');
  }

  return fetchResponse.json();
};
